import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import VehicleCard from "@/components/feature/VehicleCard";
import Reveal from "@/components/base/Reveal";
import { useVehicles } from "@/hooks/useVehicles";
import FilterSidebar, { type BrowseFilters } from "./components/FilterSidebar";
import MapView from "./components/MapView";

const ALL_CITIES = ["Nairobi", "Mombasa", "Kisumu"];
const ALL_CATEGORIES = ["SUV", "Sedan", "Van", "Hatchback", "Wagon"];

const SORT_OPTIONS = [
  { value: "recommended", label: "Recommended" },
  { value: "price-asc", label: "Price: low to high" },
  { value: "price-desc", label: "Price: high to low" },
  { value: "rating", label: "Top rated" },
];

export default function Browse() {
  const [searchParams] = useSearchParams();
  const { vehicles, loading, error, reload } = useVehicles();

  const [showFilters, setShowFilters] = useState(false);
  const [view, setView] = useState<"list" | "map">("list");
  const [sort, setSort] = useState("recommended");
  const [filters, setFilters] = useState<BrowseFilters>(() => ({
    city: searchParams.get("city") || "",
    categories: searchParams.get("category") ? [searchParams.get("category") as string] : [],
    transmission: "",
    maxPrice: 30000,
    instantBook: false,
    minSeats: null,
  }));

  const from = searchParams.get("from") || "";
  const to = searchParams.get("to") || "";

  const updateFilters = (patch: Partial<BrowseFilters>) => setFilters((prev) => ({ ...prev, ...patch }));

  const resetFilters = () =>
    setFilters({ city: "", categories: [], transmission: "", maxPrice: 30000, instantBook: false, minSeats: null });

  const activeFilterCount =
    (filters.city ? 1 : 0) +
    filters.categories.length +
    (filters.transmission ? 1 : 0) +
    (filters.maxPrice < 30000 ? 1 : 0) +
    (filters.instantBook ? 1 : 0) +
    (filters.minSeats !== null ? 1 : 0);

  const results = useMemo(() => {
    const filtered = vehicles.filter((vehicle) => {
      if (filters.city && vehicle.city !== filters.city) return false;
      if (filters.categories.length && !filters.categories.includes(vehicle.category || "")) return false;
      if (filters.transmission && vehicle.transmission !== filters.transmission) return false;
      if (Number(vehicle.price_per_day) > filters.maxPrice) return false;
      if (filters.instantBook && !vehicle.instant_book) return false;
      if (filters.minSeats !== null && (vehicle.seats ?? 0) < filters.minSeats) return false;
      return true;
    });

    const sorted = [...filtered];
    if (sort === "price-asc") sorted.sort((a, b) => Number(a.price_per_day) - Number(b.price_per_day));
    else if (sort === "price-desc") sorted.sort((a, b) => Number(b.price_per_day) - Number(a.price_per_day));
    else if (sort === "rating") sorted.sort((a, b) => Number(b.rating ?? 0) - Number(a.rating ?? 0));
    else sorted.sort((a, b) => Number(b.rating ?? 0) - Number(a.rating ?? 0));
    return sorted;
  }, [vehicles, filters, sort]);

  return (
    <div className="min-h-screen w-full bg-background-50">
      <Navbar />

      <div className="mx-auto w-full max-w-7xl px-4 pt-28 md:px-8 md:pt-36">
        <div className="max-w-3xl">
          <p className="flex items-center gap-2 text-sm text-foreground-500">
            <i className="ri-roadster-line text-primary-500"></i> Available fleet
          </p>
          <h1 className="mt-3 font-heading text-3xl font-semibold text-foreground-950 md:text-4xl">
            Cars for hire in Kenya
          </h1>
          <p className="mt-3 text-base leading-relaxed text-foreground-600">
            Compare verified cars from local hosts. Filter by city, budget and the way you drive.
          </p>
          {(from || to) && (
            <p className="mt-4 inline-flex items-center gap-2 rounded-full border border-accent-500/30 bg-accent-500/10 px-3.5 py-1.5 text-xs text-accent-300">
              <i className="ri-calendar-event-line"></i>
              Trip requested: {from || "flexible"} → {to || "flexible"}
            </p>
          )}
        </div>
      </div>

      <div className="mx-auto grid w-full max-w-7xl grid-cols-1 gap-8 px-4 py-10 md:px-8 lg:grid-cols-[300px_1fr]">
        <aside className="lg:sticky lg:top-28 lg:self-start">
          <button
            type="button"
            onClick={() => setShowFilters((prev) => !prev)}
            className="mb-4 flex w-full items-center justify-between rounded-lg border border-background-200 bg-background-100 px-4 py-3 text-sm font-medium text-foreground-900 lg:hidden"
          >
            <span className="flex items-center gap-2">
              <i className="ri-equalizer-line"></i> Filters
              {activeFilterCount > 0 && (
                <span className="rounded-full bg-primary-500 px-2 py-0.5 text-xs text-background-50">
                  {activeFilterCount}
                </span>
              )}
            </span>
            <i className={showFilters ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
          </button>

          <div className={`${showFilters ? "block" : "hidden"} lg:block`}>
            <FilterSidebar
              filters={filters}
              cities={ALL_CITIES}
              categories={ALL_CATEGORIES}
              onChange={updateFilters}
              onReset={resetFilters}
            />
          </div>
        </aside>

        <div>
          <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-foreground-600">
              {loading ? "Loading cars..." : `${results.length} ${results.length === 1 ? "car" : "cars"} found`}
            </p>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="inline-flex rounded-full bg-background-100 p-1">
              <button
                type="button"
                onClick={() => setView("list")}
                className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-medium transition-colors ${
                  view === "list" ? "bg-primary-500 text-background-50" : "text-foreground-700 hover:text-foreground-950"
                }`}
              >
                <i className="ri-list-check"></i> List
              </button>
              <button
                type="button"
                onClick={() => setView("map")}
                className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-medium transition-colors ${
                  view === "map" ? "bg-primary-500 text-background-50" : "text-foreground-700 hover:text-foreground-950"
                }`}
              >
                <i className="ri-map-2-line"></i> Map
              </button>
            </div>
            <div className="relative">
              <select
                value={sort}
                onChange={(event) => setSort(event.target.value)}
                aria-label="Sort cars"
                className="w-full cursor-pointer appearance-none rounded-lg border border-background-200 bg-background-100 py-2.5 pl-9 pr-9 text-sm text-foreground-950 focus:border-primary-500 focus:outline-none sm:w-56"
              >
                {SORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <i className="ri-sort-desc pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500"></i>
              <i className="ri-arrow-down-s-line pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-foreground-500"></i>
            </div>
            </div>
          </div>

          {error && (
            <div className="flex flex-col items-start gap-3 rounded-xl border border-secondary-700/40 bg-secondary-950/40 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="flex items-center gap-2 text-sm text-secondary-300">
                <i className="ri-error-warning-line"></i> {error}
              </p>
              <button
                type="button"
                onClick={reload}
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50"
              >
                <i className="ri-refresh-line"></i> Try again
              </button>
            </div>
          )}

          {loading && !error && (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="overflow-hidden rounded-2xl border border-background-200/70 bg-background-100">
                  <div className="h-52 w-full animate-pulse bg-background-200/60" />
                  <div className="space-y-3 p-4">
                    <div className="h-4 w-2/3 animate-pulse rounded bg-background-200/60" />
                    <div className="h-3 w-1/2 animate-pulse rounded bg-background-200/60" />
                    <div className="h-3 w-1/3 animate-pulse rounded bg-background-200/60" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && !error && view === "map" && (
            <MapView
              vehicles={results}
              city={filters.city || results[0]?.city || "Nairobi"}
              onCityChange={(nextCity) => updateFilters({ city: nextCity })}
            />
          )}

          {!loading && !error && view === "list" && results.length === 0 && (
            <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-background-200 bg-background-100 px-6 py-20 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200/60 text-3xl text-foreground-500">
                <i className="ri-search-eye-line"></i>
              </span>
              <h3 className="mt-5 font-heading text-lg font-semibold text-foreground-950">No cars match those filters</h3>
              <p className="mt-2 max-w-sm text-sm text-foreground-600">
                Try widening your price range, picking another city, or clearing a few filters.
              </p>
              <button
                type="button"
                onClick={resetFilters}
                className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50"
              >
                <i className="ri-refresh-line"></i> Clear all filters
              </button>
            </div>
          )}

          {!loading && !error && view === "list" && results.length > 0 && (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
              {results.map((vehicle, index) => (
                <Reveal key={vehicle.id} delay={(index % 3) * 80}>
                  <VehicleCard vehicle={vehicle} />
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}